class CookiesBanner extends HTMLElement {
  #banner = null;
  #prefs = null;
  #firstFocusable = null;
  #lastFocusable = null;
  #nativeObserver = null;

  connectedCallback() {
    this.#banner = this.querySelector('.cookies-banner__dialog');
    this.#prefs = this.querySelector('.cookies-prefs');

    this.addEventListener('click', this.#handleClick.bind(this));
    this.addEventListener('keydown', this.#handleKeydown.bind(this));

    this.#watchForNativeBanner();
  }

  /**
   * Start watching for the native Shopify banner immediately — before the
   * Customer Privacy API is ready. The observer both suppresses the native
   * banner and triggers our own. Once the visitor has already consented
   * (checked via the API when available) we stop watching.
   */
  #watchForNativeBanner() {
    const NATIVE_SELECTORS = ['#shopify-pc__banner', '#shopify-pc__prefs'];

    const alreadyConsented = () => {
      const api = window.Shopify?.customerPrivacy;
      if (!api) return false;
      const consent = api.currentVisitorConsent();
      return consent && Object.values(consent).some(v => v !== '');
    };

    const suppress = (el) => {
      el.setAttribute('aria-hidden', 'true');
      el.style.setProperty('display', 'none', 'important');
    };

    const handleNativeVisible = () => {
      if (alreadyConsented()) return;
      this.#nativeObserver?.disconnect();
      this.#nativeObserver = null;
      this.#syncCheckboxesToCurrentConsent();
      this.#loadCustomerPrivacyFeature();
    };

    // Check whether the native banner is already in the DOM right now.
    for (const selector of NATIVE_SELECTORS) {
      const el = document.querySelector(selector);
      if (el && getComputedStyle(el).display !== 'none') {
        suppress(el);
        handleNativeVisible();
        return;
      }
    }

    // Otherwise observe the DOM for when Shopify injects it.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          for (const selector of NATIVE_SELECTORS) {
            const match = node.matches(selector) ? node : node.querySelector(selector);
            if (!match) continue;

            suppress(match);
            handleNativeVisible();
            return;
          }
        }
      }
    });

    observer.observe(document.body ?? document.documentElement, {
      childList: true,
      subtree: true,
    });

    this.#nativeObserver = observer;
  }

  #loadCustomerPrivacyFeature() {
    window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        error => {
          if (error) {
            // Rescue error
          }
          this.#showBanner();
        },
    );
  }

  #showBanner() {
    this.#banner?.setAttribute('aria-hidden', 'false');
  }

  #hideBanner() {
    this.#banner?.setAttribute('aria-hidden', 'true');
  }

  #showPrefs() {
    this.#hideBanner();
    this.#prefs?.setAttribute('aria-hidden', 'false');
    this.#trapFocus(this.#prefs);
  }

  #hidePrefs() {
    this.#prefs?.setAttribute('aria-hidden', 'true');
    this.#releaseFocus();
  }

  #handleClick(event) {
    const action = event.target.closest('[data-action]')?.dataset.action;
    if (!action) return;

    switch (action) {
      case 'accept-all':
        this.#acceptAll();
        break;
      case 'decline-all':
        this.#declineAll();
        break;
      case 'save-choices':
        this.#saveChoices();
        break;
      case 'manage':
        this.#showPrefs();
        break;
      case 'close-prefs':
        this.#hidePrefs();
        this.#showBanner();
        break;
    }
  }

  #handleKeydown(event) {
    if (this.#prefs?.getAttribute('aria-hidden') === 'false') {
      if (event.key === 'Escape') {
        this.#hidePrefs();
        this.#showBanner();
      }
      if (event.key === 'Tab') {
        this.#manageFocusTrap(event);
      }
    }
  }

  #withPrivacyApi(fn) {
    if (window.Shopify?.customerPrivacy) {
      fn(window.Shopify.customerPrivacy);
      return;
    }
    window.addEventListener(
      'shopifyConsentTrackingApiLoaded',
      () => fn(window.Shopify.customerPrivacy),
      { once: true }
    );
  }

  #acceptAll() {
    this.#hideBanner();
    this.#hidePrefs();
    this.#withPrivacyApi((api) => {
      api.setTrackingConsent(
        { analytics: true, marketing: true, preferences: true, sale_of_data: true },
        () => {}
      );
    });
  }

  #declineAll() {
    this.#hideBanner();
    this.#hidePrefs();
    this.#withPrivacyApi((api) => {
      api.setTrackingConsent(
        { analytics: false, marketing: false, preferences: false, sale_of_data: false },
        () => {}
      );
    });
  }

  #saveChoices() {
    const analyticsMarketing = this.querySelector('[data-consent="analytics-marketing"]')?.checked ?? true;
    const preferences = this.querySelector('[data-consent="preferences"]')?.checked ?? true;
    this.#hideBanner();
    this.#hidePrefs();
    this.#withPrivacyApi((api) => {
      api.setTrackingConsent(
        { analytics: analyticsMarketing, marketing: analyticsMarketing, preferences, sale_of_data: analyticsMarketing },
        () => {}
      );
    });
  }

  #syncCheckboxesToCurrentConsent() {
    if (!window.Shopify?.customerPrivacy) return;

    const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
    if (!consent) return;

    const analyticsMarketingCheckbox = this.querySelector('[data-consent="analytics-marketing"]');
    const preferencesCheckbox = this.querySelector('[data-consent="preferences"]');

    const analyticsDecided = consent.analytics !== '';
    const preferencesDecided = consent.preferences !== '';

    if (analyticsMarketingCheckbox && analyticsDecided) {
      analyticsMarketingCheckbox.checked = consent.analytics === 'yes' || consent.marketing === 'yes';
    }
    if (preferencesCheckbox && preferencesDecided) {
      preferencesCheckbox.checked = consent.preferences === 'yes';
    }
  }

  #getFocusableElements(container) {
    return [...container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter(el => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
  }

  #trapFocus(container) {
    const focusable = this.#getFocusableElements(container);
    if (!focusable.length) return;

    this.#firstFocusable = focusable[0];
    this.#lastFocusable = focusable[focusable.length - 1];
    this.#firstFocusable.focus();
  }

  #manageFocusTrap(event) {
    if (!this.#firstFocusable || !this.#lastFocusable) return;

    if (event.shiftKey) {
      if (document.activeElement === this.#firstFocusable) {
        event.preventDefault();
        this.#lastFocusable.focus();
      }
    } else {
      if (document.activeElement === this.#lastFocusable) {
        event.preventDefault();
        this.#firstFocusable.focus();
      }
    }
  }

  #releaseFocus() {
    this.#firstFocusable = null;
    this.#lastFocusable = null;
  }

  disconnectedCallback() {
    this.#nativeObserver?.disconnect();
  }
}

customElements.define('cookies-banner', CookiesBanner);
